import { useEffect, useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { apiPrivate } from "@/lib/api";
import { Loader2, Bell, Save, Play, Send, AlertTriangle, CheckCircle2, Clock, Mail, FileText, ShieldAlert, UserX } from "lucide-react";
import { toast } from "sonner";

const TYPE_META = {
    profile_incomplete: {
        label: "Profilo incompleto",
        desc: "Email a soci con profilo sotto la soglia minima",
        icon: UserX,
        iconBg: "bg-vermillion/15 text-vermillion",
        chipBg: "bg-vermillion/15 text-vermillion",
        borderActive: "border-vermillion/40",
        ringActive: "border-vermillion/60 bg-vermillion/5",
        hasThresholdPct: true,
    },
    stale_data: {
        label: "Dati anagrafici stantii",
        desc: "Email a soci che non aggiornano profilo da N giorni",
        icon: Clock,
        iconBg: "bg-magenta/15 text-magenta",
        chipBg: "bg-magenta/15 text-magenta",
        borderActive: "border-magenta/40",
        ringActive: "border-magenta/60 bg-magenta/5",
        hasThresholdDays: true,
    },
    disclaimer_missing: {
        label: "Disclaimer mercato non accettato",
        desc: "Email a soci con quote che non hanno accettato l'ultima versione",
        icon: ShieldAlert,
        iconBg: "bg-bayside/15 text-bayside",
        chipBg: "bg-bayside/15 text-bayside",
        borderActive: "border-bayside/40",
        ringActive: "border-bayside/60 bg-bayside/5",
    },
    unread_documents: {
        label: "Documenti non letti",
        desc: "Email a soci con documenti più vecchi di N giorni mai aperti",
        icon: FileText,
        iconBg: "bg-lover/15 text-lover",
        chipBg: "bg-lover/15 text-lover",
        borderActive: "border-lover/40",
        ringActive: "border-lover/60 bg-lover/5",
        hasThresholdDays: true,
    },
};

function fmtDate(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }); }
    catch { return iso; }
}

export default function AdminReminders() {
    const [config, setConfig] = useState(null);
    const [pending, setPending] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [running, setRunning] = useState(false);
    const [notifyTarget, setNotifyTarget] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const [cfg, pend] = await Promise.all([
                apiPrivate.get("/admin/reminders/config"),
                apiPrivate.get("/admin/reminders/pending"),
            ]);
            setConfig(cfg.data);
            setPending(pend.data || []);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const updateField = (type, field, value) => {
        setConfig({ ...config, [type]: { ...config[type], [field]: value } });
    };

    const saveConfig = async () => {
        setSaving(true);
        try {
            const payload = {
                profile_incomplete: config.profile_incomplete,
                stale_data: config.stale_data,
                disclaimer_missing: config.disclaimer_missing,
                unread_documents: config.unread_documents,
            };
            const r = await apiPrivate.put("/admin/reminders/config", payload);
            setConfig(r.data);
            toast.success("Configurazione salvata");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore salvataggio");
        } finally {
            setSaving(false);
        }
    };

    const runNow = async () => {
        if (!window.confirm("Eseguire ora il job promemoria? Verranno inviate email reali ai soci con attività pendenti (rispettando cooldown).")) return;
        setRunning(true);
        try {
            const r = await apiPrivate.post("/admin/reminders/run-now");
            const sent = r.data.emails_sent || {};
            const total = Object.values(sent).reduce((a, b) => a + b, 0);
            toast.success(`Job eseguito: ${total} email inviate · ${r.data.skipped_cooldown} in cooldown`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore esecuzione job");
        } finally {
            setRunning(false);
        }
    };

    const sendToUser = async (userId, types) => {
        try {
            const r = await apiPrivate.post(`/admin/reminders/notify/${userId}`, { types });
            const sent = r.data.sent_types || [];
            const failed = r.data.failed || [];
            if (sent.length) toast.success(`Inviate ${sent.length} email`);
            if (failed.length) toast.error(`Fallite: ${failed.join(", ")}`);
            setNotifyTarget(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore invio");
        }
    };

    // KPI counts
    const kpis = {
        users: pending.length,
        actionable: pending.reduce((s, u) => s + u.pending.filter(p => !p.cooldown_active).length, 0),
        cooldown: pending.reduce((s, u) => s + u.pending.filter(p => p.cooldown_active).length, 0),
        byType: {
            profile_incomplete: pending.reduce((s, u) => s + (u.pending.some(p => p.type === "profile_incomplete" && !p.cooldown_active) ? 1 : 0), 0),
            stale_data: pending.reduce((s, u) => s + (u.pending.some(p => p.type === "stale_data" && !p.cooldown_active) ? 1 : 0), 0),
            disclaimer_missing: pending.reduce((s, u) => s + (u.pending.some(p => p.type === "disclaimer_missing" && !p.cooldown_active) ? 1 : 0), 0),
            unread_documents: pending.reduce((s, u) => s + (u.pending.some(p => p.type === "unread_documents" && !p.cooldown_active) ? 1 : 0), 0),
        },
    };

    if (loading || !config) {
        return (
            <AppLayout admin>
                <div className="p-12 text-center text-zinc-500"><Loader2 className="h-6 w-6 animate-spin inline" /></div>
            </AppLayout>
        );
    }

    return (
        <AppLayout admin>
            <div className="max-w-[1400px] mx-auto" data-testid="admin-reminders">
                <div className="flex items-end justify-between gap-4 flex-wrap">
                    <div>
                        <span className="label-tag text-magenta">Promemoria automatici</span>
                        <h1 className="font-display text-5xl mt-3 tracking-tighter">
                            Ricorda ai soci <span className="font-serif-italic text-gradient-tropical">cosa fare</span>.
                        </h1>
                        <p className="text-zinc-400 mt-3 max-w-2xl">
                            Configura periodicità e soglie dei promemoria automatici, monitora chi ha attività in sospeso e invia email a singoli soci on-demand.
                            Il job gira ogni giorno alle 09:15 UTC e rispetta i cooldown per non spammare.
                        </p>
                    </div>
                    <button onClick={runNow} disabled={running} className="btn-rebel" data-testid="run-now-btn">
                        {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                        Esegui ora
                    </button>
                </div>

                {/* KPI cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
                    <div className="bento p-5" data-testid="kpi-users">
                        <div className="text-xs text-zinc-500 uppercase tracking-widest">Soci con pendenze</div>
                        <div className="text-4xl font-display tracking-tighter mt-2">{kpis.users}</div>
                    </div>
                    <div className="bento p-5" data-testid="kpi-actionable">
                        <div className="text-xs text-zinc-500 uppercase tracking-widest">Azioni inviabili</div>
                        <div className="text-4xl font-display tracking-tighter mt-2 text-vermillion">{kpis.actionable}</div>
                    </div>
                    <div className="bento p-5" data-testid="kpi-cooldown">
                        <div className="text-xs text-zinc-500 uppercase tracking-widest">In cooldown</div>
                        <div className="text-4xl font-display tracking-tighter mt-2 text-zinc-400">{kpis.cooldown}</div>
                    </div>
                    <div className="bento p-5" data-testid="kpi-config-updated">
                        <div className="text-xs text-zinc-500 uppercase tracking-widest">Ultima modifica config</div>
                        <div className="text-sm mt-2 text-zinc-300">{fmtDate(config.updated_at)}</div>
                        <div className="text-xs text-zinc-500 mt-1 truncate">{config.updated_by || "—"}</div>
                    </div>
                </div>

                {/* Config */}
                <div className="bento p-8 mt-8">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h2 className="text-2xl font-display tracking-tighter">Configurazione tipi di promemoria</h2>
                            <p className="text-sm text-zinc-500 mt-1">Cooldown = giorni minimi tra due email dello stesso tipo allo stesso socio.</p>
                        </div>
                        <button onClick={saveConfig} disabled={saving} className="btn-rebel" data-testid="save-config-btn">
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                            Salva configurazione
                        </button>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                        {Object.keys(TYPE_META).map(type => {
                            const meta = TYPE_META[type];
                            const cfg = config[type];
                            const Icon = meta.icon;
                            return (
                                <div key={type} className={`rounded-2xl p-5 bg-zinc-900/40 border ${cfg.enabled ? meta.borderActive : "border-zinc-800"} transition`} data-testid={`cfg-${type}`}>
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="flex items-start gap-3">
                                            <div className={`h-10 w-10 rounded-full ${meta.iconBg} flex items-center justify-center`}>
                                                <Icon className="h-5 w-5" />
                                            </div>
                                            <div>
                                                <div className="font-semibold">{meta.label}</div>
                                                <div className="text-xs text-zinc-500 mt-1">{meta.desc}</div>
                                                <div className="text-xs text-zinc-400 mt-1">
                                                    Pendenti ora: <strong className="text-white">{kpis.byType[type]}</strong>
                                                </div>
                                            </div>
                                        </div>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={cfg.enabled}
                                                onChange={e => updateField(type, "enabled", e.target.checked)}
                                                className="accent-magenta h-4 w-4"
                                                data-testid={`cfg-${type}-enabled`}
                                            />
                                            <span className="text-xs text-zinc-400">{cfg.enabled ? "Attivo" : "Off"}</span>
                                        </label>
                                    </div>

                                    <div className="grid grid-cols-2 gap-3 mt-4">
                                        {meta.hasThresholdPct && (
                                            <div>
                                                <label className="text-xs text-zinc-500">Soglia % minima</label>
                                                <input
                                                    type="number"
                                                    min={0}
                                                    max={100}
                                                    value={cfg.threshold_pct ?? ""}
                                                    onChange={e => updateField(type, "threshold_pct", Number(e.target.value))}
                                                    className="w-full mt-1 bg-zinc-900 rounded-lg px-3 py-2 text-sm border border-zinc-800 focus:border-magenta outline-none"
                                                    data-testid={`cfg-${type}-threshold-pct`}
                                                />
                                            </div>
                                        )}
                                        {meta.hasThresholdDays && (
                                            <div>
                                                <label className="text-xs text-zinc-500">Soglia giorni</label>
                                                <input
                                                    type="number"
                                                    min={1}
                                                    max={3650}
                                                    value={cfg.threshold_days ?? ""}
                                                    onChange={e => updateField(type, "threshold_days", Number(e.target.value))}
                                                    className="w-full mt-1 bg-zinc-900 rounded-lg px-3 py-2 text-sm border border-zinc-800 focus:border-magenta outline-none"
                                                    data-testid={`cfg-${type}-threshold-days`}
                                                />
                                            </div>
                                        )}
                                        <div className={meta.hasThresholdPct || meta.hasThresholdDays ? "" : "col-span-2"}>
                                            <label className="text-xs text-zinc-500">Cooldown (giorni)</label>
                                            <input
                                                type="number"
                                                min={1}
                                                max={365}
                                                value={cfg.cooldown_days ?? ""}
                                                onChange={e => updateField(type, "cooldown_days", Number(e.target.value))}
                                                className="w-full mt-1 bg-zinc-900 rounded-lg px-3 py-2 text-sm border border-zinc-800 focus:border-magenta outline-none"
                                                data-testid={`cfg-${type}-cooldown`}
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Pending report */}
                <div className="bento p-8 mt-8">
                    <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
                        <div>
                            <h2 className="text-2xl font-display tracking-tighter">Report soci con attività pendenti</h2>
                            <p className="text-sm text-zinc-500 mt-1">Solo soci attivi con almeno una pendenza basata sulla configurazione corrente.</p>
                        </div>
                        <div className="text-xs text-zinc-500 flex items-center gap-2">
                            <CheckCircle2 className="h-3.5 w-3.5 text-bayside" /> azionabile
                            <Clock className="h-3.5 w-3.5 ml-3 text-zinc-500" /> cooldown attivo
                        </div>
                    </div>

                    {pending.length === 0 ? (
                        <div className="text-center text-zinc-500 py-12" data-testid="pending-empty">
                            <CheckCircle2 className="h-10 w-10 mx-auto mb-3 text-bayside opacity-70" />
                            Nessun socio con attività pendenti. Configurazione applicata correttamente.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="text-xs text-zinc-500 uppercase tracking-widest">
                                    <tr className="border-b border-zinc-800">
                                        <th className="text-left py-3">Socio</th>
                                        <th className="text-left py-3">Quote</th>
                                        <th className="text-left py-3">Profilo</th>
                                        <th className="text-left py-3">Pendenze</th>
                                        <th className="text-right py-3">Azione</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pending.map(u => (
                                        <tr key={u.user_id} className="border-b border-zinc-900/60 hover:bg-zinc-900/30" data-testid={`pending-row-${u.user_id}`}>
                                            <td className="py-3">
                                                <div className="font-semibold">{u.display_name}</div>
                                                <div className="text-xs text-zinc-500">{u.email}</div>
                                            </td>
                                            <td className="py-3">
                                                {u.quota_categories.length ? (
                                                    <div className="flex gap-1">
                                                        {u.quota_categories.map(q => (
                                                            <span key={q} className="px-2 py-0.5 rounded-full bg-bayside/15 text-bayside text-xs">{q}</span>
                                                        ))}
                                                    </div>
                                                ) : <span className="text-zinc-600 text-xs">—</span>}
                                            </td>
                                            <td className="py-3">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-20 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                                                        <div className="h-full bg-gradient-to-r from-vermillion via-magenta to-lover" style={{ width: `${u.profile_completion}%` }} />
                                                    </div>
                                                    <span className="text-xs text-zinc-400">{u.profile_completion}%</span>
                                                </div>
                                            </td>
                                            <td className="py-3">
                                                <div className="flex flex-wrap gap-1.5">
                                                    {u.pending.map(p => {
                                                        const meta = TYPE_META[p.type];
                                                        return (
                                                            <span
                                                                key={p.type}
                                                                title={p.detail + (p.last_reminder_at ? ` · ultimo invio: ${fmtDate(p.last_reminder_at)}` : "")}
                                                                className={`px-2 py-0.5 rounded-full text-xs flex items-center gap-1 ${p.cooldown_active ? "bg-zinc-800 text-zinc-500" : meta.chipBg}`}
                                                            >
                                                                {p.cooldown_active ? <Clock className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
                                                                {meta.label}
                                                            </span>
                                                        );
                                                    })}
                                                </div>
                                            </td>
                                            <td className="py-3 text-right">
                                                <button
                                                    onClick={() => setNotifyTarget(u)}
                                                    disabled={!u.pending.some(p => !p.cooldown_active)}
                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs bg-gradient-to-r from-vermillion via-magenta to-lover text-white disabled:opacity-40 disabled:cursor-not-allowed"
                                                    data-testid={`notify-btn-${u.user_id}`}
                                                >
                                                    <Send className="h-3 w-3" /> Invia
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Notify modal */}
                {notifyTarget && (
                    <NotifyModal
                        user={notifyTarget}
                        onClose={() => setNotifyTarget(null)}
                        onSend={sendToUser}
                    />
                )}
            </div>
        </AppLayout>
    );
}

function NotifyModal({ user, onClose, onSend }) {
    const actionable = user.pending.filter(p => !p.cooldown_active);
    const [selected, setSelected] = useState(actionable.map(p => p.type));
    const [sending, setSending] = useState(false);

    const toggle = (t) => {
        setSelected(s => s.includes(t) ? s.filter(x => x !== t) : [...s, t]);
    };

    const submit = async () => {
        if (!selected.length) return;
        setSending(true);
        await onSend(user.user_id, selected);
        setSending(false);
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="notify-modal">
            <div className="bento p-6 max-w-lg w-full" onClick={e => e.stopPropagation()}>
                <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-full bg-magenta/15 text-magenta flex items-center justify-center">
                        <Mail className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                        <h3 className="text-xl font-display tracking-tighter">Invia promemoria</h3>
                        <p className="text-xs text-zinc-500 mt-1">{user.display_name} · {user.email}</p>
                    </div>
                </div>
                <p className="text-sm text-zinc-400 mt-4">Seleziona uno o più tipi di promemoria da inviare. Verranno ignorati i tipi in cooldown.</p>
                <div className="space-y-2 mt-4">
                    {user.pending.map(p => {
                        const meta = TYPE_META[p.type];
                        return (
                            <label key={p.type} className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition ${p.cooldown_active ? "border-zinc-800 opacity-50 cursor-not-allowed" : selected.includes(p.type) ? meta.ringActive : "border-zinc-800 hover:border-zinc-700"}`}>
                                <input
                                    type="checkbox"
                                    disabled={p.cooldown_active}
                                    checked={selected.includes(p.type)}
                                    onChange={() => toggle(p.type)}
                                    className="mt-0.5 accent-magenta h-4 w-4"
                                    data-testid={`notify-check-${p.type}`}
                                />
                                <div className="flex-1">
                                    <div className="text-sm font-semibold">{meta.label}</div>
                                    <div className="text-xs text-zinc-500 mt-0.5">{p.detail}</div>
                                    {p.cooldown_active && <div className="text-xs text-zinc-600 mt-1">In cooldown · ultimo invio: {fmtDate(p.last_reminder_at)}</div>}
                                </div>
                            </label>
                        );
                    })}
                </div>
                <div className="flex justify-end gap-2 mt-6">
                    <button onClick={onClose} className="btn-ghost" data-testid="notify-cancel">Annulla</button>
                    <button onClick={submit} disabled={!selected.length || sending} className="btn-rebel" data-testid="notify-send">
                        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        Invia {selected.length || ""}
                    </button>
                </div>
            </div>
        </div>
    );
}
